import { useState, useCallback, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOriginalError } from 'services/AxiosService';
import { isMobile, useWindowSize } from 'app/spacing';
import Loader from 'components/Kizen/Loader';
import { getSearchParam, setSearchParams } from 'hooks/useSearchParam';
import { getOrderingParam, getSortValue } from 'utility/SortingHelpers';
import Automation2Service, {
  getAutomationObjectName,
} from 'services/Automation2Service';
import {
  camelToSnakeCase,
  snakeToCamelCase,
  snakeToCamelCaseKeys,
} from 'services/helpers';
import {
  buildPage,
  updatePageConfig,
  getAutomations,
  updateAutomation,
  duplicateAutomation,
  deleteAutomation,
  cleanToast,
  resetFilters,
  setFilterName,
  createOrUpdateGroup,
  removeGroup,
  changeGroupAction,
  changeDirectoryAction,
  deleteFolderAction,
  selectAllNonRootFolders,
  selectCurrentDirectory,
  selectRootFolder,
  selectValidParentFolders,
  applySearchAction,
  createFolderSuccess,
  editFolderSuccess,
  editAutomationDescriptionSuccess,
  getAutomationsAndFolders,
} from 'store/automationsPage/automations.redux';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';
import NewAutomationModal from './NewAutomationModal';
import { actions } from './Cells';
import { toastVariant, useToast } from 'components/ToastProvider';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { useDebounce } from 'react-use';
import { maybeTrackSearch } from 'utility/analytics';
import { DEFAULT_DELAY } from 'utility/config';
import { deferExecution } from 'utility/defer';
import { AutomationHistoryModal } from './AutomationHistoryModal';
import { EditDescriptionModal } from './EditDescriptionModal';
import useModal from 'components/Modals/useModal';
import { useSyncDispatch } from 'ts-components/hooks/useSyncDispatch';
import {
  EditAutomationFolderModal,
  EditFolderModal,
  NewFolderModal,
} from './EditFolderModal';
import AxiosService from 'services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import {
  createAutomationFolder,
  deleteAutomationFolder,
  patchAutomation,
  patchAutomationFolder,
} from '@kizen/api/automation';
import useCan from 'hooks/useCan';
import { KizenError } from 'services/errors';
import { KizenTypography } from 'app/typography';

const defaultPageSettings = {
  pageNumber: 1,
  pageSize: 50,
};

const updateAutomationHandler = async ({ isAction, ...data }) => {
  try {
    return await Automation2Service.update(data);
  } catch (error) {
    const originalError = getOriginalError(error);
    if (originalError && originalError.name) {
      throw new Error(originalError.name);
    }

    if ('active' in data && !isAction) {
      throw new Error(originalError);
    }

    return null;
  }
};

const StyledText = styled(KizenTypography)`
  word-break: break-word;
  white-space: pre-wrap;
`;

const AutomationPage = ({ title }) => {
  const hasAccess = useCan({
    view: 'sections',
    for: 'automations_section',
  });
  const canEdit = useCan({
    edit: 'sections',
    for: 'automations_section.automations',
  });
  const canDelete = useCan({
    remove: 'sections',
    for: 'automations_section.automations',
  });
  if (!hasAccess) {
    throw new KizenError(403);
  }

  const { t } = useTranslation();
  useSetTitleOnLoad(title(t));
  const history = useHistory();
  const dispatch = useDispatch();
  const syncDispatch = useSyncDispatch();
  const { width } = useWindowSize();
  const [deletedAutomation, setAutomationForDelete] = useState(null);
  const [historyAutomation, setHistoryAutomation] = useState(null);
  const [showAddModal, setShowAddModal] = useState(null);
  const [showAddFolderModal, setShowAddFolderModal] = useState(null);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [automationToEdit, setAutomationToEdit] = useState(null);
  const [descriptionToEdit, setDescriptionToEdit] = useState(null);
  const [isMenuOpen, toggleMenu] = useState(false);
  const [showToast] = useToast();

  const isFetching = useSelector((s) => s.automationPage.isFetching);
  const groups = useSelector((s) => s.automationPage.groups);
  const pageConfig = useSelector((s) => s.automationPage.pageConfig);
  const models = useSelector((s) => s.automationPage.models);
  const toastData = useSelector((s) => s.automationPage.toastData);
  const filters = useSelector((s) => s.automationPage.filters);
  const currentDirectory = useSelector(selectCurrentDirectory);
  const rootFolder = useSelector(selectRootFolder);
  const nonRootFolders = useSelector(selectAllNonRootFolders);
  const validParentFolders = useSelector(selectValidParentFolders);

  // Track debounced changes to the search term and send to google analytics
  const { search } = pageConfig;
  useDebounce(() => maybeTrackSearch(search, 'automations'), DEFAULT_DELAY, [
    search,
  ]);

  useEffect(() => {
    const sort = getSearchParam(history.location, 'sort');
    const group = getSearchParam(history.location, 'group');
    const search = getSearchParam(history.location, 'q');
    const page = getSearchParam(history.location, 'page');
    const size = getSearchParam(history.location, 'size');
    const folderId = getSearchParam(history.location, 'folder');

    syncDispatch(
      buildPage({
        t: t,
        groups: {},
        page: {
          search: search || '',
          group,
          sort,
          page: page && parseInt(page, 10),
          size: size && parseInt(size, 10),
        },
        folderId,
      })
    );
  }, [syncDispatch, history, t]);

  useEffect(() => {
    setSearchParams(history, {
      folder:
        rootFolder.id === currentDirectory.id ? null : currentDirectory.id,
    });
  }, [history, rootFolder, currentDirectory]);

  // memo functions ------------------------------------------------
  const sortValue = useMemo(
    () => getSortValue(snakeToCamelCase(pageConfig.sort)),
    [pageConfig.sort]
  );

  // filter handlers -----------------------------------------------
  const updateFilterName = useCallback(
    (name) => {
      dispatch(setFilterName({ name }));
    },
    [dispatch]
  );

  const applyFiltersAction = useCallback(
    (payload) => {
      dispatch(
        updatePageConfig({
          page: 1,
          filterObject: payload,
        })
      );
      dispatch(
        getAutomations({
          updatePageConfig: true,
        })
      );
    },
    [dispatch]
  );

  const saveGroupAction = useCallback(
    (data) => {
      dispatch(createOrUpdateGroup({ ...data, history }));
    },
    [dispatch, history]
  );

  const deleteGroupAction = useCallback(() => {
    dispatch(removeGroup({ history }));
    toggleMenu(false);
  }, [dispatch, history]);

  const clearFilter = useCallback(() => {
    dispatch(resetFilters());
    dispatch(
      getAutomations({
        updatePageConfig: true,
      })
    );
    deferExecution(() => {
      setSearchParams(history, {
        page: null,
        ...(pageConfig.group ? { group: null } : {}),
        ...(pageConfig.search ? { q: '' } : {}),
      });
    });
  }, [dispatch, history, pageConfig.group, pageConfig.search]);

  // table handlers ------------------------------------------------
  const handleChangeGroupId = useCallback(
    ({ value }) => {
      const data = value === 'none' ? null : value;
      dispatch(changeGroupAction({ id: value }));
      dispatch(getAutomations());
      deferExecution(() => {
        setSearchParams(history, {
          group: data,
          page: null,
        });
      });
    },
    [history, dispatch]
  );

  const handleChangeSearch = useCallback(
    (value) => {
      dispatch(applySearchAction({ history, search: value }));
    },
    [history, dispatch]
  );

  const handleChangePageNumber = useCallback(
    (value) => {
      dispatch(updatePageConfig({ page: value }));
      dispatch(getAutomations());
      deferExecution(() => {
        setSearchParams(history, {
          page: value,
        });
      });
    },
    [history, dispatch]
  );

  const handleChangePageSize = useCallback(
    (value) => {
      dispatch(updatePageConfig({ size: value }));
      dispatch(getAutomations());
      deferExecution(() => {
        setSearchParams(history, {
          size: value,
        });
      });
    },
    [history, dispatch]
  );

  const handleSortTable = useCallback(
    ({ column, direction }) => {
      const data = camelToSnakeCase(column);
      const value = getOrderingParam({
        column: data,
        direction,
      });

      dispatch(updatePageConfig({ sort: value }));
      dispatch(getAutomations());
      deferExecution(() => {
        setSearchParams(history, {
          sort: value,
        });
      });
    },
    [dispatch, history]
  );

  // columns handlers
  const handleUpdateAutomation = useCallback(
    async (data) => {
      const automation = await updateAutomationHandler(data);

      if (automation) {
        dispatch(
          updateAutomation({
            ...automation,
            customObjectName: getAutomationObjectName(automation),
            status: automation.active,
          })
        );
      }
    },
    [dispatch]
  );

  const handleChangeDirectory = useCallback(
    (folder) => {
      dispatch(changeDirectoryAction({ folder, page: 1 }));
    },
    [dispatch]
  );

  const handleAddFolder = useCallback(
    async ({ name, parent_folder_id }) => {
      const res = await createAutomationFolder(AxiosService, {
        name,
        parent_folder_id,
      });

      const folder = {
        automations_count: 0,
        created: res.created,
        id: res.id,
        name: res.name,
        parent_folder_id: res.parent_folder_id,
        updated: res.updated,
      };

      dispatch(createFolderSuccess({ folder }));
      dispatch(getAutomationsAndFolders());

      return folder;
    },
    [dispatch]
  );

  const [historyModalProps, , historyModal] = useModal({
    handleHide: () => setHistoryAutomation(null),
  });

  const handleConfirmAddFolder = useCallback(
    async (folder) => {
      try {
        await handleAddFolder(folder);
        setShowAddFolderModal(null);
      } catch (error) {
        showToast({
          message:
            getErrorMessage(getOriginalError(error)) ||
            t('Error saving folder'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [handleAddFolder, showToast, t]
  );

  const handleDeleteAutomation = useCallback(() => {
    dispatch(cleanToast());
    dispatch(
      deleteAutomation({
        id: deletedAutomation.id,
        t,
      })
    );
    setAutomationForDelete(null);
  }, [dispatch, deletedAutomation, t]);

  const handleEditFolder = useCallback(
    async (folder) => {
      try {
        const { id, ...edits } = folder;

        const response = await patchAutomationFolder(AxiosService, id, edits);

        dispatch(
          editFolderSuccess({
            folder: response,
            edits,
          })
        );
        dispatch(getAutomationsAndFolders());

        setFolderToEdit(null);
      } catch (error) {
        showToast({
          message:
            getErrorMessage(getOriginalError(error)) ||
            t('Error saving folder'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [dispatch, showToast, t]
  );

  const handleEditAutomationFolder = useCallback(
    async ({ folderId }) => {
      try {
        await patchAutomation(AxiosService, automationToEdit.id, {
          folder_id: folderId,
          last_revision: automationToEdit.revision,
        });

        dispatch(getAutomationsAndFolders());

        setAutomationToEdit(null);
      } catch (error) {
        showToast({
          message:
            getErrorMessage(getOriginalError(error)) ||
            t('Error saving agentic workflow'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [dispatch, showToast, t, automationToEdit]
  );

  const handleEditAutomationDescription = useCallback(
    async ({ userDescription }) => {
      try {
        const response = await patchAutomation(
          AxiosService,
          descriptionToEdit.id,
          {
            user_description: userDescription,
            last_revision: descriptionToEdit.revision,
          }
        );

        dispatch(
          editAutomationDescriptionSuccess({
            automation: snakeToCamelCaseKeys(response),
          })
        );

        setDescriptionToEdit(null);
      } catch (error) {
        showToast({
          message:
            getErrorMessage(getOriginalError(error)) ||
            t('Error saving agentic workflow'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [descriptionToEdit?.id, descriptionToEdit?.revision, dispatch, showToast, t]
  );

  const handleSelectAction = useCallback(
    async ({ value }, automationOrFolder, parentFolder) => {
      switch (value) {
        case actions.edit: {
          history.push(`/agentic-workflow/${automationOrFolder.id}/`);
          break;
        }
        case actions.edit_automation_folder: {
          setAutomationToEdit(automationOrFolder);
          break;
        }
        case actions.activate: {
          handleUpdateAutomation({
            id: automationOrFolder.id,
            active: true,
            isAction: true,
            lastRevision: automationOrFolder.revision,
          });
          break;
        }
        case actions.deactivate: {
          handleUpdateAutomation({
            id: automationOrFolder.id,
            active: false,
            isAction: true,
            lastRevision: automationOrFolder.revision,
          });
          break;
        }
        case actions.duplicate: {
          dispatch(duplicateAutomation(automationOrFolder.id));
          break;
        }
        case actions.history: {
          setHistoryAutomation(automationOrFolder);
          historyModal.show();
          break;
        }
        case actions.delete: {
          setAutomationForDelete(automationOrFolder);
          break;
        }
        case actions.edit_folder: {
          setFolderToEdit({
            folder: automationOrFolder,
            parentFolder: parentFolder || currentDirectory,
          });
          break;
        }
        case actions.delete_folder: {
          setFolderToDelete(automationOrFolder);
          break;
        }

        case actions.edit_description: {
          setDescriptionToEdit(automationOrFolder);
          break;
        }

        case actions.run: {
          try {
            const { execution } = await Automation2Service.start({
              automationId: automationOrFolder.id,
              type: 'global',
            });

            if (execution?.id) {
              window.open(
                `/agentic-workflow/${automationOrFolder.id}?history=${execution.id}`,
                '_blank',
                'noopener,noreferrer'
              );
            }
          } catch (error) {
            showToast({
              variant: toastVariant.FAILURE,
              message: t(`The agentic workflow could not be started.`),
            });
          }
          break;
        }

        case actions.new_automation: {
          setShowAddModal({ folder: automationOrFolder || currentDirectory });
          break;
        }

        case actions.new_folder: {
          setShowAddFolderModal({
            folder: automationOrFolder || currentDirectory,
          });
          break;
        }
      }
    },
    [
      history,
      handleUpdateAutomation,
      dispatch,
      historyModal,
      showToast,
      setShowAddModal,
      currentDirectory,
      t,
    ]
  );

  useEffect(() => {
    if (toastData) {
      showToast(toastData);
      dispatch(cleanToast());
    }
  }, [dispatch, toastData, showToast]);

  if (isFetching) {
    return <Loader loading />;
  }

  return (
    <>
      {isMobile(width) ? (
        <MobileLayout
          groups={groups}
          groupId={pageConfig.group}
          handleChangeGroup={handleChangeGroupId}
          search={pageConfig.search}
          handleChangeSearch={handleChangeSearch}
          pageNumber={pageConfig.page || defaultPageSettings.pageNumber}
          pageSize={pageConfig.size || defaultPageSettings.pageSize}
          handleChangePageNumber={handleChangePageNumber}
          handleChangePageSize={handleChangePageSize}
          sort={sortValue}
          handleChangeSort={handleSortTable}
          handleSelectAction={handleSelectAction}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      ) : (
        <DesktopLayout
          groups={groups}
          groupId={pageConfig.group}
          handleChangeGroup={handleChangeGroupId}
          search={pageConfig.search}
          handleChangeSearch={handleChangeSearch}
          pageNumber={pageConfig.page || defaultPageSettings.pageNumber}
          pageSize={pageConfig.size || defaultPageSettings.pageSize}
          handleChangePageNumber={handleChangePageNumber}
          handleChangePageSize={handleChangePageSize}
          sort={sortValue}
          handleChangeSort={handleSortTable}
          handleUpdateAutomation={handleUpdateAutomation}
          handleSelectAction={handleSelectAction}
          handleChangeDirectory={handleChangeDirectory}
          openMenu={isMenuOpen}
          toggleMenu={toggleMenu}
          filterInfo={filters}
          applyFiltersAction={applyFiltersAction}
          saveGroupAction={saveGroupAction}
          deleteGroupAction={deleteGroupAction}
          setFilterName={updateFilterName}
          clearFilter={clearFilter}
          errorsReturned={filters.errors}
          data-qa="automations-table"
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}
      {!!deletedAutomation && (
        <ConfirmDeletionModal
          show
          onConfirm={handleDeleteAutomation}
          onHide={() => setAutomationForDelete(null)}
          buttonText={t('Confirm Archive')}
          heading={t('Please Confirm Archive')}
          size="medium"
        >
          <StyledText>
            {deletedAutomation?.customObject?.objectName
              ? `${t(
                  'The ‘{{ automationName }}’ agentic workflow for {{ objectName }} will be archived.',
                  {
                    automationName: deletedAutomation.name,
                    objectName: deletedAutomation.customObject.objectName,
                  }
                )}\n${t('Remaining steps in any active execution will not run.')}`
              : `${t(
                  'The ‘{{ automationName }}’ agentic workflow will be archived.',
                  {
                    automationName: deletedAutomation?.name,
                  }
                )}\n${t('Remaining steps in any active execution will not run.')}`}
          </StyledText>
        </ConfirmDeletionModal>
      )}
      {showAddModal && (
        <NewAutomationModal
          currentDirectory={showAddModal.folder}
          show
          models={models}
          folders={nonRootFolders}
          validParentFolders={validParentFolders}
          onHide={() => setShowAddModal(null)}
          handleCreate={() => {
            dispatch(getAutomationsAndFolders());
            setShowAddModal(null);
          }}
          onAddFolder={handleAddFolder}
          rootFolderId={rootFolder.id}
        />
      )}
      {showAddFolderModal ? (
        <NewFolderModal
          currentDirectory={showAddFolderModal.folder}
          folders={validParentFolders}
          onAddFolder={handleAddFolder}
          onConfirm={handleConfirmAddFolder}
          onHide={() => setShowAddFolderModal(null)}
        />
      ) : null}
      {folderToEdit && (
        <EditFolderModal
          parentDirectory={folderToEdit.parentFolder}
          folder={folderToEdit.folder}
          folders={validParentFolders}
          rootFolderId={rootFolder.id}
          onHide={() => setFolderToEdit(null)}
          onConfirm={handleEditFolder}
          onAddFolder={handleAddFolder}
        />
      )}
      {automationToEdit && (
        <EditAutomationFolderModal
          automation={automationToEdit}
          currentDirectory={currentDirectory}
          folders={nonRootFolders}
          validParentFolders={validParentFolders}
          rootFolderId={rootFolder.id}
          onHide={() => setAutomationToEdit(null)}
          onConfirm={handleEditAutomationFolder}
          onAddFolder={handleAddFolder}
        />
      )}
      {folderToDelete && (
        <ConfirmDeletionModal
          show
          actionBtnColor="red"
          onHide={() => setFolderToDelete(null)}
          onConfirm={async () => {
            try {
              await deleteAutomationFolder(AxiosService, folderToDelete.id);

              dispatch(deleteFolderAction({ id: folderToDelete.id }));
              setFolderToDelete(null);
            } catch (error) {
              showToast({
                message:
                  getErrorMessage(getOriginalError(error)) ||
                  t('Error deleting folder'),
                variant: toastVariant.FAILURE,
              });
            }
          }}
        >
          {t(
            'Are you sure you want to delete this folder? The agentic workflows within the folder will be moved up one folder level (they will not be deleted).'
          )}
        </ConfirmDeletionModal>
      )}
      {historyAutomation ? (
        <AutomationHistoryModal
          automation={historyAutomation}
          modalProps={historyModalProps}
        />
      ) : null}
      {descriptionToEdit ? (
        <EditDescriptionModal
          automation={descriptionToEdit}
          onConfirm={handleEditAutomationDescription}
          onHide={() => setDescriptionToEdit(null)}
        />
      ) : null}
    </>
  );
};

export default AutomationPage;
